import { plural } from './domain/format'
import { Fragment, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { HashRouter, NavLink, useLocation } from 'react-router-dom'
import { useReports } from './state/reports'
import { RouteMemory } from './state/route'
import { useOnline } from './state/offline'
import { usePalette } from './state/palette'
import { useProjects } from './state/projects'
import { useTheme } from './state/theme'
import { useContrast } from './state/contrast'
import { useAccent } from './state/accent'
import { Button } from './ui/Button'
import { Icon, type IconName } from './ui/Icon'
import { Footer } from './ui/Footer'
import { Notice } from './ui/primitives'
import { SkipLink } from './ui/SkipLink'
import { HelpDialog } from './ui/HelpDialog'
import { FeedbackDialog } from './ui/FeedbackDialog'
import { OnboardingDialog } from './ui/OnboardingDialog'
import { TourOverlay } from './ui/TourOverlay'
import type { Tour } from './tours'
import { useOnboardingChecklist } from './state/onboarding'
import { UndoNotice } from './ui/UndoNotice'
import { ErrorBoundary } from './ui/ErrorBoundary'
import { Brand, Topbar } from './ui/Topbar'
import { useMobileNav } from './ui/useMobileNav'
import { Announcer } from './ui/Announcer'
import { focusMain } from './ui/focus'
import { useCatalog } from './state/catalogState'
import { REGISTRY_ID } from './domain/catalog'
import { AppRoutes } from './AppRoutes'
import { PAGES, PRODUCT_NAME, SECTIONS, isAvailable, pageFor } from './pages'

export default function App() {
  return (
    <ErrorBoundary>
      <HashRouter>
        <Shell />
      </HashRouter>
    </ErrorBoundary>
  )
}

function Shell() {
  const state = useReports()
  const catalog = useCatalog(state)
  const [theme, setTheme] = useTheme()
  const [contrast, setContrast] = useContrast()
  const [accent, setAccent] = useAccent()
  const [palette, setPalette] = usePalette()
  const projects = useProjects()
  const hasData = state.reports.length > 0
  const home = hasData ? '/assessment' : '/welcome'
  const online = useOnline()
  const mobileNav = useMobileNav()
  const [helpOpen, setHelpOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [tour, setTour] = useState<Tour | null>(null)
  const [onboardingOpen, setOnboardingOpen] = useState(false)
  const openOnboarding = () => setOnboardingOpen(true)
  const location = useLocation()
  const checklist = useOnboardingChecklist(
    {
      sample: hasData,
      sources: catalog.sources.some((s) => s.enabled !== false),
    },
    {
      settings: location.pathname === '/settings',
    },
  )

  const softwareCount = useMemo(
    () => new Set(state.reports.map((r) => r.software.key)).size,
    [state.reports],
  )
  const currentPage = pageFor(location.pathname)
  // A shared link (/assessment/<name>) is no page of the navigation; it is named here once.
  const sharedLink = location.pathname.startsWith('/assessment/')
  const pageLabel = sharedLink ? 'Shared assessment' : (currentPage?.label ?? 'Assessment')

  // On each page change: name the page in the browser tab, and move focus to
  // the content, as a full page load would. Not on the first render, where
  // focus belongs where the browser put it.
  const firstPath = useRef(true)
  useEffect(() => {
    document.title = `${pageLabel}, ${PRODUCT_NAME}`
    if (firstPath.current) {
      firstPath.current = false
      return
    }
    document.getElementById('main')?.focus({ preventScroll: true })
    // Every new address moves focus, even between two pages with the same label.
    // oxlint-disable-next-line react/exhaustive-effect-dependencies
  }, [location.pathname, pageLabel])

  /** The navigation's entries for one section, only those this session can open. */
  const navItems = (section: string) =>
    PAGES.filter(
      (page) => page.section === section && isAvailable(page, state.reports.length, softwareCount),
    ).map((page) => (
      <NavItem
        key={page.path}
        to={page.path}
        label={page.label}
        icon={page.icon}
        active={currentPage?.path === page.path}
        tally={
          page.path === '/data' && state.reports.length > 0
            ? {
                count: state.reports.length,
                label: `${state.reports.length} ${plural(state.reports.length, 'assessment')} loaded`,
              }
            : page.path === '/projects' && projects.projects.length > 0
              ? {
                  count: projects.projects.length,
                  label: `${projects.projects.length} ${plural(projects.projects.length, 'project')}`,
                }
              : undefined
        }
        onNavigate={mobileNav.close}
      />
    ))

  return (
    <div className="app">
      <SkipLink />
      <RouteMemory enabled={hasData} />

      <div className="brand">
        <Brand to={home} />
      </div>

      <Topbar
        restoring={state.restoring}
        busy={state.busy}
        online={online}
        theme={theme}
        onTheme={setTheme}
        onHelp={() => setHelpOpen(true)}
        onFeedback={() => setFeedbackOpen(true)}
        onGuide={openOnboarding}
        guideRemaining={checklist.remaining}
        navOpen={mobileNav.open}
        onToggleNav={mobileNav.toggle}
        navToggleRef={mobileNav.toggleRef}
        home={home}
      />

      <div
        className={`nav-backdrop${mobileNav.open ? ' is-open' : ''}`}
        data-open={mobileNav.open}
        aria-hidden="true"
        onClick={mobileNav.close}
      />

      <nav
        className={`nav${mobileNav.open ? ' is-open' : ''}`}
        data-open={mobileNav.open}
        id="navigation"
        aria-label="Application navigation"
      >
        <div className="nav-brand-header">
          <Brand to={home} onNavigate={mobileNav.close} />
        </div>
        {/* One scrolling body for every section; preferences sit apart at the foot.
            Empty while the session is restored: which pages are offered depends
            on it, and drawing the empty-session list first shifts the rest down. */}
        <div className="nav-body">
          {!state.restoring &&
            SECTIONS.filter((section) => section !== 'Preferences').map((section) => {
              const items = navItems(section)
              return items.length === 0 ? null : (
                <Fragment key={section}>
                  <div className="nav-heading">{section}</div>
                  {items}
                </Fragment>
              )
            })}
        </div>
        <div className="nav-footer">{navItems('Preferences')}</div>
      </nav>

      {/* While the mobile drawer is open, the content behind it is out of reach;
          the header stays usable, since its toggle closes the drawer. */}
      <main className="main" id="main" tabIndex={-1} inert={mobileNav.open}>
        <div className="main-inner">
          {state.storageError && (
            <Notice tone="warn">
              <div className="row justify-between">
                <span>{state.storageError}</span>
                <Button
                  tone="quiet"
                  size="small"
                  label="Dismiss"
                  onClick={() => {
                    focusMain()
                    state.dismissStorageError()
                  }}
                />
              </div>
            </Notice>
          )}

          {!state.restoring && (
            <Announcer reportCount={state.reports.length} syncMessage={catalog.syncMessage} />
          )}

          <UndoNotice
            removed={state.removed}
            onUndo={state.undoRemove}
            onDone={state.forgetRemoved}
          />

          {state.failures.length > 0 && (
            <Notice tone="error">
              <div className="row justify-between">
                <strong>Not everything could be loaded</strong>
                <Button
                  tone="quiet"
                  size="small"
                  icon="close"
                  label="Dismiss"
                  onClick={() => {
                    focusMain()
                    state.dismissFailures()
                  }}
                />
              </div>
              <ul className="m-0 mt-2 list-indent">
                {state.failures.map((failure) => (
                  <li key={`${failure.ref}-${failure.reason}`}>
                    <code>{failure.ref}</code> {failure.reason}
                  </li>
                ))}
              </ul>
            </Notice>
          )}

          {state.restoring ? (
            <RestoringPlaceholder />
          ) : (
            <Suspense fallback={<RestoringPlaceholder />}>
              <AppRoutes
                state={state}
                catalog={catalog}
                projects={projects}
                theme={theme}
                onTheme={setTheme}
                contrast={contrast}
                onContrast={setContrast}
                accent={accent}
                onAccent={setAccent}
                palette={palette}
                onPalette={setPalette}
                softwareCount={softwareCount}
                guide={{
                  remaining: checklist.remaining,
                  total: checklist.total,
                  open: openOnboarding,
                }}
              />
              {/* Inside the boundary, so it is drawn with the page: drawn under a
                  placeholder, it sat in view and was then pushed out (layout shift). */}
              <Footer />
            </Suspense>
          )}
        </div>
      </main>

      <HelpDialog
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        reportCount={state.reports.length}
        onOpenGuide={openOnboarding}
        // A guide that needs assessments loads the samples when there are none.
        onStartTour={async (next) => {
          if (next.needsData && !hasData) await state.addSamples()
          setTour(next)
        }}
      />

      {tour && <TourOverlay key={tour.id} tour={tour} onEnd={() => setTour(null)} />}

      <FeedbackDialog
        isOpen={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        route={location.pathname + location.search}
        reportCount={state.reports.length}
        softwareCount={softwareCount}
        theme={theme}
        sources={catalog.sources.filter((s) => s.enabled !== false).map((s) => s.name)}
      />

      <OnboardingDialog
        isOpen={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        state={state}
        checklist={checklist}
        registry={{
          following: catalog.sources.some((s) => s.id === REGISTRY_ID && s.enabled !== false),
          follow: () => catalog.toggleSource(REGISTRY_ID, true),
        }}
      />
    </div>
  )
}

/**
 * Shown while the stored session is being read back.
 *
 * Deliberately the shape of what is coming, so the page does not jump when the
 * real content arrives, and so a returning visitor never sees the empty state
 * flash up as though their data had been lost.
 */
function RestoringPlaceholder() {
  return (
    <div className="stack" role="status" aria-busy="true">
      <span className="visually-hidden">Loading your session</span>
      <div className="grid grid-tiles">
        {[0, 1, 2, 3].map((n) => (
          <div key={n} className="tile">
            <div className="skeleton" style={{ height: '0.7rem', width: '45%' }} />
            <div
              className="skeleton"
              style={{ height: '1.6rem', width: '60%', marginTop: '0.6rem' }}
            />
          </div>
        ))}
      </div>
      <div className="card">
        <div className="card-body stack">
          {[0, 1, 2, 3, 4].map((n) => (
            <div key={n} className="skeleton" style={{ height: '1.5rem' }} />
          ))}
        </div>
      </div>
    </div>
  )
}

function NavItem({
  to,
  label,
  icon,
  active,
  tally,
  onNavigate,
}: {
  to: string
  label: string
  icon: IconName
  /** Set by the caller, so a page's other addresses (a tab, an alias) count too. */
  active: boolean
  /** A count beside the label, and what it counts, said in full to screen readers. */
  tally?: { count: number; label: string }
  onNavigate?: () => void
}) {
  return (
    <NavLink
      to={to}
      className="nav-item"
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
    >
      <Icon name={icon} size={16} />
      <span>{label}</span>
      {tally && (
        <span className="tally" title={tally.label}>
          <span aria-hidden="true">{tally.count}</span>
          <span className="visually-hidden">, {tally.label}</span>
        </span>
      )}
    </NavLink>
  )
}
